import { API_ROUTES, postEncryptedJson } from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import { TransactionType } from "../../utils/enums";
import { extractWalletTradingId } from "../../utils/transactionAccountId";

/**
 * Transaction Details Controller
 * Wraps the encrypted get-transaction endpoint and normalizes the result
 * into a UI-friendly view model (loosely mirroring the Flutter TransactionModel).
 *
 * API (logical, before encryption):
 * POST {{base_url}}/v1/user/get-transaction
 * BODY:
 * {
 *   "TRANSACTION_ID": "txbb0c989368b4"
 * }
 *
 * Encryption, device headers, and Authorization: Bearer {token}
 * are handled centrally by apiClient.postEncryptedJson.
 */
class TransactionDetailsController {
  /**
   * Fetch a single transaction by TRANSACTION_ID.
   *
   * @param {string} transactionId
   * @param {Object} [options] - postEncryptedJson options (e.g. { baseURL })
   * @returns {Promise<{
   *   id: string | null,
   *   typeCode: number | null,
   *   statusCode: number | null,
   *   typeLabel: string | null,
   *   statusLabel: string | null,
   *   amount: number | null,
   *   adminFees: number | null,
   *   agentFees: number | null,
   *   amountAfterFees: number | null,
   *   currencySymbol: string | null,
   *   currencyCode: string | null,
   *   paymentMethod: string | null,
   *   transactionDate: string | null,
   *   motive: string | null,
   *   senderName: string | null,
   *   receiverName: string | null,
   *   senderWalletLabel: string | null,
   *   receiverWalletLabel: string | null,
   *   country: string | null,
   *   ipAddress: string | null,
   *   refTransactionId: string | null,
   *   raw: any
   * }>}
   */
  static async getTransactionDetails(transactionId, options = {}) {
    if (!transactionId) {
      throw new Error("TRANSACTION_ID is required");
    }

    const body = {
      TRANSACTION_ID: transactionId,
    };

    const res = await postEncryptedJson(API_ROUTES.GET_TRANSACTION, body, options);
    const d = res.decryptedData || res.data || {};

    try {
      // logging removed
    } catch (_) {
      // ignore logging failures
    }

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    // Flutter uses TransactionModel.fromJson(responce["DATA"])
    const raw =
      d.DATA ||
      d.data ||
      d.TRANSACTION ||
      d.transaction ||
      null;

    if (!raw || typeof raw !== "object") {
      throw new Error("Missing transaction data in response");
    }

    const id =
      raw.TRANSACTION_ID?.toString?.() ??
      raw.REF_ID?.toString?.() ??
      raw.ID?.toString?.() ??
      null;

    const transactionDate =
      raw.TRANSACTION_DATE ||
      raw.TRX_DATE ||
      raw.CREATED_AT ||
      raw.DATE ||
      null;

    const typeCode =
      typeof raw.TYPE === "number"
        ? raw.TYPE
        : typeof raw.TYPE === "string"
        ? Number(raw.TYPE)
        : typeof raw.type === "number"
        ? raw.type
        : null;

    const statusCode =
      typeof raw.STATUS === "number"
        ? raw.STATUS
        : typeof raw.STATUS === "string"
        ? Number(raw.STATUS)
        : typeof raw.status === "number"
        ? raw.status
        : null;

    const amount =
      typeof raw.AMOUNT === "number"
        ? raw.AMOUNT
        : raw.AMOUNT != null
        ? Number(raw.AMOUNT)
        : null;

    const adminFees =
      typeof raw.ADMIN_FEES === "number"
        ? raw.ADMIN_FEES
        : raw.ADMIN_FEES != null
        ? Number(raw.ADMIN_FEES)
        : null;

    const agentFees =
      typeof raw.AGENT_FEES === "number"
        ? raw.AGENT_FEES
        : raw.AGENT_FEES != null
        ? Number(raw.AGENT_FEES)
        : null;

    const totalFees =
      (typeof adminFees === "number" ? adminFees : 0) +
      (typeof agentFees === "number" ? agentFees : 0);

    const amountAfterFees =
      typeof amount === "number" ? amount - totalFees : null;

    // Currency object comes from the same backend JSON the Flutter app uses:
    // raw["currencies"]["CURRENCY_SYMBOL"] etc.
    const currencyObj =
      raw.currencies ||
      raw.CURRENCY ||
      raw.currency ||
      null;

    const currencySymbol =
      currencyObj?.CURRENCY_SYMBOL ||
      currencyObj?.currencySymbol ||
      currencyObj?.SYMBOL ||
      null;

    const currencyCode =
      currencyObj?.CURRENCY_CODE ||
      currencyObj?.currencyCode ||
      currencyObj?.CODE ||
      null;

    const parseLocalizedLabel = (value) => {
      if (value == null || value === "") return null;
      if (typeof value === "object") {
        return value.en || value.EN || value.ar || value.METHOD_NAME || value.NAME || null;
      }
      const text = String(value).trim();
      if (!text) return null;
      if (text.startsWith("{")) {
        try {
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed === "object") {
            return parsed.en || parsed.EN || parsed.ar || Object.values(parsed)[0] || text;
          }
        } catch (_) {
          return text;
        }
      }
      return text;
    };

    const requestRaw = raw.transaction_request || raw.TRANSACTION_REQUEST;
    const request = requestRaw && typeof requestRaw === "object" ? requestRaw : {};
    const methodObject =
      raw.payment_methods ||
      raw.PAYMENT_METHOD ||
      raw.PaymentMethod ||
      request.payment_methods ||
      request.PAYMENT_METHOD ||
      null;

    const paymentMethod =
      parseLocalizedLabel(raw.METHOD_LABEL) ||
      parseLocalizedLabel(raw.METHOD_NAME) ||
      parseLocalizedLabel(raw.PAYMENT_METHOD_LABEL) ||
      parseLocalizedLabel(raw.PAYMENT_METHOD_NAME) ||
      parseLocalizedLabel(methodObject?.METHOD_LABEL) ||
      parseLocalizedLabel(methodObject?.METHOD_NAME) ||
      parseLocalizedLabel(methodObject?.NAME) ||
      parseLocalizedLabel(request.METHOD_LABEL) ||
      parseLocalizedLabel(request.METHOD_NAME) ||
      null;

    const motive = raw.MOTIVE || raw.motive || null;
    const phoneNumber =
      raw.PHONE_NUMBER ||
      raw.PHONE ||
      raw.MOBILE ||
      raw.MOBILE_NUMBER ||
      (raw.transaction_request && (
        raw.transaction_request.PHONE_NUMBER ||
        raw.transaction_request.PHONE ||
        raw.transaction_request.MOBILE ||
        raw.transaction_request.MOBILE_NUMBER
      )) ||
      null;
    const country = raw.SENDER_COUNTRY || raw.RECEIVER_COUNTRY || null;
    const ipAddress = raw.IP_ADDRESS || raw.ip_address || null;
    const refTransactionId = raw.REF_TRANSACTION_ID || raw.RefTransactionId || null;

    // Sender / receiver names & wallet labels (simplified mirror of Flutter helpers)
    const senderWallet = raw.sender_wallet || raw.SENDER_WALLET || null;
    const receiverWallet = raw.receiver_wallet || raw.RECEIVER_WALLET || null;

    const senderAccount =
      senderWallet?.account ||
      senderWallet?.ACCOUNT ||
      null;
    const receiverAccount =
      receiverWallet?.account ||
      receiverWallet?.ACCOUNT ||
      null;

    const senderUser =
      senderAccount?.user_login ||
      senderAccount?.USER_LOGIN ||
      senderAccount?.user ||
      senderAccount?.USER ||
      null;
    const receiverUser =
      receiverAccount?.user_login ||
      receiverAccount?.USER_LOGIN ||
      receiverAccount?.user ||
      receiverAccount?.USER ||
      null;

    const senderName =
      senderUser?.NICK_NAME ||
      senderUser?.USER_NAME ||
      senderUser?.FULL_NAME ||
      raw.SENDER_NAME ||
      null;
    const receiverName =
      receiverUser?.NICK_NAME ||
      receiverUser?.USER_NAME ||
      receiverUser?.FULL_NAME ||
      raw.RECEIVER_NAME ||
      null;

    const senderTradingId = extractWalletTradingId(senderWallet);
    const receiverTradingId = extractWalletTradingId(receiverWallet);

    const senderCurrency =
      senderWallet?.currencies ||
      senderWallet?.CURRENCY ||
      senderWallet?.currency ||
      null;
    const receiverCurrency =
      receiverWallet?.currencies ||
      receiverWallet?.CURRENCY ||
      receiverWallet?.currency ||
      null;

    const senderWalletLabel = (() => {
      if (!senderCurrency) return null;
      const code = senderCurrency.CURRENCY_CODE || senderCurrency.currencyCode;
      return code ? `${code} wallet` : null;
    })();

    const receiverWalletLabel = (() => {
      if (!receiverCurrency) return null;
      const code = receiverCurrency.CURRENCY_CODE || receiverCurrency.currencyCode;
      return code ? `${code} wallet` : null;
    })();

    // Basic type/status labels; UI may further customize with enums
    const typeLabel =
      raw.TYPE_LABEL ||
      raw.TYPE_NAME ||
      null;

    const statusLabel =
      raw.STATUS_LABEL ||
      raw.STATUS_NAME ||
      (typeof raw.STATUS === "string" ? raw.STATUS : null);

    return {
      id,
      typeCode,
      statusCode,
      typeLabel,
      statusLabel,
      amount,
      adminFees,
      agentFees,
      amountAfterFees,
      currencySymbol,
      currencyCode,
      paymentMethod,
      transactionDate,
      motive,
      phoneNumber,
      senderName,
      receiverName,
      senderTradingId,
      receiverTradingId,
      senderWalletLabel,
      receiverWalletLabel,
      country,
      ipAddress,
      refTransactionId,
      raw,
    };
  }
}

export default TransactionDetailsController;

